import {
    BaseSdkClient,
} from "@mrflark/core/api";
import type {
    AddAllowedCountryRequest,
    AddKnownIpRequest,
    AdminToken,
    AllowedCountry,
    AuditFilters,
    AuditLog,
    AuthIdentity,
    ContactInfo,
    CreateTokenRequest,
    CreateTokenResponse,
    CreateUserRequest,
    ImpersonationStatus,
    KnownIp,
    LoginRequest,
    Session,
    UpdateUserRequest,
} from "./types.ts";

const DEFAULT_AUTH_GATEWAY_ENDPOINT = "https://auth.mrflark.org";

export type CreateAuthGatewayClientOptions = {
    endpoint?: string;
    onUnauthorized?: () => void;
    authToken?: string;
};

export class AuthGatewayClient extends BaseSdkClient {
    constructor(options: CreateAuthGatewayClientOptions) {
        super({
            endpoint: options.endpoint || DEFAULT_AUTH_GATEWAY_ENDPOINT,
            onUnauthorized: options.onUnauthorized,
            routePrefix: "/api",
            authToken: options.authToken,
        });
    }

    // reads the non-httpOnly csrf cookie set by the auth gateway on login
    private getCsrfToken = (): string | undefined => {
        if (typeof document === "undefined") return undefined;
        const match = document.cookie
            .split("; ")
            .find((row: string) => row.startsWith("homelab_csrf="));
        return match?.split("=")[1];
    };

    private csrfHeaders = (): Record<string, string> | undefined => {
        const csrfToken = this.getCsrfToken();
        if (!csrfToken) return undefined;
        return { "x-csrf-token": csrfToken };
    };

    public override post = <T>(
        operation: string,
        body?: unknown,
    ): Promise<T> => {
        return this.request<T>(operation, {
            method: "POST",
            body,
            headers: this.csrfHeaders(),
        });
    };

    public override patch = <T>(
        operation: string,
        body?: unknown,
    ): Promise<T> => {
        return this.request<T>(operation, {
            method: "PATCH",
            body,
            headers: this.csrfHeaders(),
        });
    };

    public override del = <T>(operation: string): Promise<T> => {
        return this.request<T>(operation, {
            method: "DELETE",
            headers: this.csrfHeaders(),
        });
    };

    public getLoginUrl = (
        redirectUri?: string,
    ): string => {
        const loginUrl = `${this.endpoint}/login`;

        if (!redirectUri) {
            return loginUrl;
        }

        return `${loginUrl}?redirect=${encodeURIComponent(redirectUri)}`;
    };

    public login = (request: LoginRequest): Promise<AuthIdentity> =>
        this.post<AuthIdentity>("/auth/login", request);

    public getMe = (): Promise<AuthIdentity> =>
        this.get<AuthIdentity>("/auth/me");

    public getUser = (userId: number): Promise<AuthIdentity> =>
        this.get<AuthIdentity>(`/users/${userId}`);

    public getUserContact = (userId: number): Promise<ContactInfo> =>
        this.get<ContactInfo>(`/users/${userId}/contact`);

    public getUsers = (): Promise<AuthIdentity[]> =>
        this.get<AuthIdentity[]>("/users");

    public createUser = (data: CreateUserRequest): Promise<AuthIdentity> =>
        this.post<AuthIdentity>("/users", data);

    public updateUser = (
        userId: number,
        data: UpdateUserRequest,
    ): Promise<AuthIdentity> =>
        this.patch<AuthIdentity>(`/users/${userId}`, data);

    public resetPassword = (userId: number, password: string): Promise<void> =>
        this.post(`/users/${userId}/password`, { newPassword: password });

    public deleteUser = (userId: number): Promise<void> =>
        this.del(`/users/${userId}`);

    public getTokens = (): Promise<AdminToken[]> =>
        this.get<AdminToken[]>("/tokens");

    public createToken = (
        data: CreateTokenRequest,
    ): Promise<CreateTokenResponse> =>
        this.post<CreateTokenResponse>("/tokens", data);

    public revokeToken = (tokenId: number): Promise<void> =>
        this.del(`/tokens/${tokenId}`);

    public deleteTokenPermanently = (tokenId: number): Promise<void> =>
        this.del(`/tokens/${tokenId}/permanent`);

    public getSessions = (): Promise<Session[]> =>
        this.get<Session[]>("/sessions");

    public revokeSession = (sessionId: string): Promise<void> =>
        this.del(`/sessions/${sessionId}`);

    public getAuditLogs = (filters: AuditFilters): Promise<AuditLog[]> =>
        this.get<AuditLog[]>("/audit", filters);

    public getImpersonationStatus = (): Promise<ImpersonationStatus> =>
        this.get<ImpersonationStatus>("/impersonation/status");

    public startImpersonation = (
        userId: number,
    ): Promise<ImpersonationStatus> =>
        this.post<ImpersonationStatus>("/impersonation/start", { userId });

    public getAllowedCountries = (): Promise<AllowedCountry[]> =>
        this.get<AllowedCountry[]>("/allowed-countries");

    public addAllowedCountry = (
        data: AddAllowedCountryRequest,
    ): Promise<AllowedCountry> =>
        this.post<AllowedCountry>("/allowed-countries", data);

    public removeAllowedCountry = (id: number): Promise<void> =>
        this.del(`/allowed-countries/${id}`);

    public getKnownIps = (): Promise<KnownIp[]> =>
        this.get<KnownIp[]>("/known-ips");

    public addKnownIp = (data: AddKnownIpRequest): Promise<KnownIp> =>
        this.post<KnownIp>("/known-ips", data);

    public removeKnownIp = (id: number): Promise<void> =>
        this.del(`/known-ips/${id}`);
}
